import {
  faPaperPlane,
  faPlus,
  faPrint,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import React, { useEffect, useState } from 'react';
import { Col, Form, Modal, Row, Tab, Tabs, Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { UPDATE_JOB } from 'services/api/job';
import * as XLSX from 'xlsx';
import { SendToShopModal } from './SendToShopModal';
import { ToolWorkOrderModalProps } from './types/types';

export const ToolWorkOrderModal: React.FC<ToolWorkOrderModalProps> = ({
  show,
  handleClose,
  job,
  jobStages
}) => {
  const [activeTab, setActiveTab] = useState<string>('tool-0');
  const [toolOrders, setToolOrders] = useState<any[]>([]);
  const [showSendToShopModal, setShowSendToShopModal] =
    useState<boolean>(false);
  const [selectedToolIndex, setSelectedToolIndex] = useState<number>(0);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (job?.toolWorkOrders && job.toolWorkOrders.length > 0) {
      setToolOrders(job.toolWorkOrders);
    } else {
      setToolOrders([createEmptyToolOrder()]);
    }
  }, [job]);

  const createEmptyToolOrder = () => ({
    toolType: 'Bladed Hole Opener',
    toolBodySize: '8',
    cutterType: 'Crushed Carbide',
    quantityToBuild: 1,
    notes: '',
    pilotHoleSize: '',
    openSize: '',
    mudWeight: '',
    casingSize: '',
    shoeDepth: '',
    td: '',
    holeAngle: 'Vertical',
    flowRate: '',
    formationType: '',
    truckingCo: '',
    scopeOfWork: 'Hole Opening',
    topConnection: '',
    lowerConnection: '',
    gluedLowerSub: 'No',
    gluedBullNose: 'Yes',
    strapWeldedBullNose: 'No',
    floatValve: 'No',
    jetsInBody: ['16', '16', '16', '-', '-', '-'],
    jetsInTopSub: ['-', '-', '-', '-', '-', '-'],
    jetsInBullNose: ['16', '-', '-', '-', '-', '-'],
    status: 'Draft'
  });

  const handleInputChange = (index: number, field: string, value: any) => {
    const updatedOrders = [...toolOrders];
    updatedOrders[index] = { ...updatedOrders[index], [field]: value };
    setToolOrders(updatedOrders);
  };

  const handleJetChange = (
    toolIndex: number,
    type: 'jetsInBody' | 'jetsInTopSub' | 'jetsInBullNose',
    jetIndex: number,
    value: string
  ) => {
    const updatedOrders = [...toolOrders];
    updatedOrders[toolIndex][type][jetIndex] = value;
    setToolOrders(updatedOrders);
  };

  const updateJobMutation = useMutation(UPDATE_JOB, {
    onSuccess: () => {
      queryClient.invalidateQueries('getJobs');
      toast.success('Work Order Saved Successfully');
    },
    onError: () => {
      toast.error('Failed to save Work Order');
    }
  });

  const handleSave = async () => {
    try {
      const stagePayload = job.jobStage
        ? { _id: (job.jobStage as any).id || (job.jobStage as any)._id }
        : undefined;

      await updateJobMutation.mutateAsync({
        jobId: ((job as any).id || job._id) as string,
        formData: {
          toolWorkOrders: toolOrders,
          jobStage: stagePayload
        } as any
      });
    } catch (e) {
      console.error(e);
      toast.error('Failed to save Work Order: ' + (e as any)?.message);
    }
  };

  const handleAddTool = () => {
    const currentTool = toolOrders[selectedToolIndex] || createEmptyToolOrder();
    const duplicatedTool = { ...currentTool, status: 'Draft' };
    const newToolOrders = [...toolOrders, duplicatedTool];
    setToolOrders(newToolOrders);
    const newIndex = newToolOrders.length - 1;
    setActiveTab(`tool-${newIndex}`);
    setSelectedToolIndex(newIndex);
    toast.success('Tool Added Successfully');
  };

  const handlePrint = () => {
    const wb = XLSX.utils.book_new();
    const wsData = [
      ['Job Type', job.jobType?.name, 'Reservation #', ''],
      [
        'Date',
        new Date().toLocaleDateString(),
        'Pilot Hole Size',
        toolOrders[selectedToolIndex].pilotHoleSize
      ],
      [
        'Company',
        job.customer?.organizationName,
        'Open Size',
        toolOrders[selectedToolIndex].openSize
      ],
      ['Well', job.well, 'Mud Weight', toolOrders[selectedToolIndex].mudWeight]
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Work Order');
    XLSX.writeFile(wb, `WorkOrder-${job.jobNumber}.xlsx`);
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Tool Work Order</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <Row className="mb-2">
            <Col md={3} className="fw-bold">
              Job Type:
            </Col>
            <Col md={3}>{job.jobType?.name}</Col>
            <Col md={3} className="fw-bold">
              Reservation #:
            </Col>
            <Col md={3}>{/* Reservation ID placeholder */}</Col>
          </Row>
          {/* Read only Job Details Header */}
          <Row className="mb-2">
            <Col md={3} className="fw-bold">
              Company:
            </Col>
            <Col md={3}>{job.customer?.organizationName}</Col>
            <Col md={3} className="fw-bold">
              Rig:
            </Col>
            <Col md={3}>{job.rig?.name}</Col>
          </Row>
          <Row className="mb-2">
            <Col md={3} className="fw-bold">
              Well:
            </Col>
            <Col md={3}>{job.well}</Col>
            <Col md={3} className="fw-bold">
              Location:
            </Col>
            <Col md={3}>
              {typeof job.location === 'string'
                ? job.location
                : job.location?.location}
            </Col>
          </Row>
        </div>
        <hr />

        <Tabs
          activeKey={activeTab}
          onSelect={k => {
            setActiveTab(k || 'tool-0');
            setSelectedToolIndex(parseInt((k || 'tool-0').split('-')[1]));
          }}
          className="mb-3"
        >
          {toolOrders.map((tool, index) => (
            <Tab
              eventKey={`tool-${index}`}
              title={`Tool ${index + 1}`}
              key={index}
            >
              <div className="p-2">
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Pilot Hole Size</Form.Label>
                      <Form.Control
                        value={tool.pilotHoleSize}
                        onChange={e =>
                          handleInputChange(
                            index,
                            'pilotHoleSize',
                            e.target.value
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Open Size</Form.Label>
                      <Form.Control
                        value={tool.openSize}
                        onChange={e =>
                          handleInputChange(index, 'openSize', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Mud Weight</Form.Label>
                      <Form.Control
                        value={tool.mudWeight}
                        onChange={e =>
                          handleInputChange(index, 'mudWeight', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Casing Size</Form.Label>
                      <Form.Control
                        value={tool.casingSize}
                        onChange={e =>
                          handleInputChange(index, 'casingSize', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Shoe Depth</Form.Label>
                      <Form.Control
                        value={tool.shoeDepth}
                        onChange={e =>
                          handleInputChange(index, 'shoeDepth', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>TD</Form.Label>
                      <Form.Control
                        value={tool.td}
                        onChange={e =>
                          handleInputChange(index, 'td', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Hole Angle</Form.Label>
                      <Form.Control
                        value={tool.holeAngle}
                        onChange={e =>
                          handleInputChange(index, 'holeAngle', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Flow Rate</Form.Label>
                      <Form.Control
                        value={tool.flowRate}
                        onChange={e =>
                          handleInputChange(index, 'flowRate', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Formation Type</Form.Label>
                      <Form.Control
                        value={tool.formationType}
                        onChange={e =>
                          handleInputChange(
                            index,
                            'formationType',
                            e.target.value
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Trucking Co</Form.Label>
                      <Form.Control
                        value={tool.truckingCo}
                        onChange={e =>
                          handleInputChange(index, 'truckingCo', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Scope of Work</Form.Label>
                      <Form.Control
                        value={tool.scopeOfWork}
                        onChange={e =>
                          handleInputChange(
                            index,
                            'scopeOfWork',
                            e.target.value
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Quantity to Build</Form.Label>
                      <Form.Control
                        type="number"
                        value={tool.quantityToBuild}
                        onChange={e =>
                          handleInputChange(
                            index,
                            'quantityToBuild',
                            parseInt(e.target.value)
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <hr />
                <h6>Tool Work Order</h6>
                <Row className="mb-3">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Tool Type</Form.Label>
                      <Form.Control
                        value={tool.toolType}
                        onChange={e =>
                          handleInputChange(index, 'toolType', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Body Size</Form.Label>
                      <Form.Control
                        value={tool.toolBodySize}
                        onChange={e =>
                          handleInputChange(
                            index,
                            'toolBodySize',
                            e.target.value
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Cutter Type</Form.Label>
                      <Form.Control
                        value={tool.cutterType}
                        onChange={e =>
                          handleInputChange(index, 'cutterType', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Jets */}
                <h6>Jets</h6>
                <Row className="mb-3">
                  <Col md={2}>Body</Col>
                  {tool.jetsInBody.map((jet: string, jIndex: number) => (
                    <Col key={`body-${jIndex}`}>
                      <Form.Control
                        size="sm"
                        value={jet}
                        onChange={e =>
                          handleJetChange(
                            index,
                            'jetsInBody',
                            jIndex,
                            e.target.value
                          )
                        }
                      />
                    </Col>
                  ))}
                </Row>
                <Row className="mb-3">
                  <Col md={2}>Top Sub</Col>
                  {tool.jetsInTopSub.map((jet: string, jIndex: number) => (
                    <Col key={`top-${jIndex}`}>
                      <Form.Control
                        size="sm"
                        value={jet}
                        onChange={e =>
                          handleJetChange(
                            index,
                            'jetsInTopSub',
                            jIndex,
                            e.target.value
                          )
                        }
                      />
                    </Col>
                  ))}
                </Row>
                <Row className="mb-3">
                  <Col md={2}>Bull Nose</Col>
                  {tool.jetsInBullNose.map((jet: string, jIndex: number) => (
                    <Col key={`bull-${jIndex}`}>
                      <Form.Control
                        size="sm"
                        value={jet}
                        onChange={e =>
                          handleJetChange(
                            index,
                            'jetsInBullNose',
                            jIndex,
                            e.target.value
                          )
                        }
                      />
                    </Col>
                  ))}
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={tool.notes}
                    onChange={e =>
                      handleInputChange(index, 'notes', e.target.value)
                    }
                  />
                </Form.Group>
              </div>
            </Tab>
          ))}
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handlePrint}>
          <FontAwesomeIcon icon={faPrint} className="me-2" /> Print
        </Button>
        <Button variant="outline-primary" onClick={handleAddTool}>
          <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Another Tool
        </Button>
        <Button
          variant="secondary"
          onClick={handleSave}
          disabled={updateJobMutation.isLoading}
        >
          {updateJobMutation.isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
                style={{ width: 20, height: 20 }}
              />
              Saving...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faSave} className="me-2" /> Save Draft
            </>
          )}
        </Button>
        <Button
          variant="primary"
          onClick={() => setShowSendToShopModal(true)}
          disabled={updateJobMutation.isLoading}
        >
          <FontAwesomeIcon icon={faPaperPlane} className="me-2" /> Send to Shop
        </Button>
      </Modal.Footer>

      <SendToShopModal
        show={showSendToShopModal}
        onHide={() => setShowSendToShopModal(false)}
        onConfirm={() => {
          handleInputChange(selectedToolIndex, 'status', 'Sent to Shop');

          const sentToShopStage = jobStages.find(
            s => s.name === 'Sent to Shop'
          );
          if (sentToShopStage) {
            updateJobMutation
              .mutateAsync({
                jobId: ((job as any).id || job._id) as string,
                formData: {
                  toolWorkOrders: [
                    ...toolOrders.map((t, i) =>
                      i === selectedToolIndex
                        ? { ...t, status: 'Sent to Shop' }
                        : t
                    )
                  ],
                  jobStage: {
                    _id: (sentToShopStage as any).id || sentToShopStage._id
                  }
                } as any
              })
              .then(() => {
                setShowSendToShopModal(false);
                handleClose();
              });
          } else {
            handleSave();
            setShowSendToShopModal(false);
          }
        }}
      />
    </Modal>
  );
};
